import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FaWallet, FaPercentage, FaMoneyBillWave, FaUsers, FaEye, FaEyeSlash, FaHistory, FaPlus } from 'react-icons/fa';
import { getVendorWalletSummary, getVendorCommissionSettings } from '../../features/vendor/vendorWalletSlice';
import { toast } from 'react-toastify';
import axios from 'axios';
import { API_URL } from '../../config/apiConfig';

const VendorWalletDisplay = ({ vendorId, onNavigateToWallet }) => {
  const dispatch = useDispatch();
  const [showBalances, setShowBalances] = useState(true);
  const [showAddMoneyModal, setShowAddMoneyModal] = useState(false);
  const [addMoneyAmount, setAddMoneyAmount] = useState("");

  const {
    driverWallets,
    totalWalletBalance,
    totalCommissionEarned,
    vendorWalletBalance,
    loading,
    error,
    commissionSettings: commissionData,
    loadingCommission,
  } = useSelector((state) => state.vendorWallet);

  useEffect(() => {
    if (vendorId) {
      dispatch(getVendorWalletSummary(vendorId));
      dispatch(getVendorCommissionSettings(vendorId));
    }
  }, [dispatch, vendorId]);

  // Auto-refresh on commission-rate updates from other components/tabs
  useEffect(() => {
    const onCustom = (e) => {
      if (!e?.detail || e.detail.vendorId !== vendorId) return;
      dispatch(getVendorWalletSummary(vendorId));
      dispatch(getVendorCommissionSettings(vendorId));
    };
    const onStorage = (e) => {
      if (e.key !== 'vendorCommissionRateUpdated') return;
      try {
        const data = JSON.parse(e.newValue || '{}');
        if (data.vendorId === vendorId) {
          dispatch(getVendorWalletSummary(vendorId));
          dispatch(getVendorCommissionSettings(vendorId));
        }
      } catch {}
    };
    window.addEventListener('vendor-commission-updated', onCustom);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('vendor-commission-updated', onCustom);
      window.removeEventListener('storage', onStorage);
    };
  }, [dispatch, vendorId]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  // Use real commission data from API - no hardcoded fallbacks
  const getCommissionRates = () => {
    if (!commissionData) {
      // Loading state - return minimal values
      return {
        vendorRate: 0,
        adminRate: 0,
        driverRate: 0
      };
    }

    const { commissionStructure, commissionSettings } = commissionData;

    // Use real rates from API response
    const vendorRate = commissionSettings?.effectiveRate || commissionStructure?.vendorRate || 0;
    const adminRate = commissionStructure?.adminRate || 0;
    const driverRate = commissionStructure?.driverRate || (100 - adminRate - vendorRate);


    return {
      vendorRate,
      adminRate,
      driverRate
    };
  };

  const handleAddMoney = async () => {
    if (!addMoneyAmount || parseFloat(addMoneyAmount) < 100) {
      toast.error("Please enter a valid amount (minimum ₹100)");
      return;
    }

    if (!window.Razorpay) {
      toast.error("Payment gateway is not loaded. Please refresh and try again.");
      return;
    }

    try {
      const token = localStorage.getItem('vendorToken');
      
      // 1. Create Razorpay order
      const response = await axios.post(`${API_URL}/payment/vendor/create-topup-order`, {
        amount: parseFloat(addMoneyAmount)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.data.success || !response.data.order) {
        toast.error("Failed to initiate payment");
        return;
      }

      const order = response.data.order;

      // 2. Open Razorpay modal
      const options = {
        key: response.data.key || process.env.REACT_APP_RAZORPAY_KEY || process.env.REACT_APP_RAZORPAY_KEY_ID || "rzp_test_PnSgiqE2elzEYx",
        amount: order.amount,
        currency: order.currency,
        name: "CabZigo Vendor",
        description: `Top up vendor wallet`,
        order_id: order.id,
        handler: async (response) => {
          try {
            // 3. Verify payment
            const verifyResponse = await axios.post(`${API_URL}/payment/vendor/verify-topup`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              amount: parseFloat(addMoneyAmount)
            }, {
              headers: { Authorization: `Bearer ${token}` }
            });

            if (verifyResponse.data.success) {
              toast.success(`₹${addMoneyAmount} added to your wallet`);
              setShowAddMoneyModal(false);
              setAddMoneyAmount("");

              // Refresh wallet data
              dispatch(getVendorWalletSummary(vendorId));
            } else {
              toast.error("Payment verification failed");
            }
          } catch (error) {
            console.error("Error verifying payment:", error);
            toast.error("Failed to verify payment");
          }
        },
        prefill: {
          name: "Vendor",
          email: "",
          contact: ""
        },
        theme: {
          color: "#2563eb"
        }
      };

      const razorpayInstance = new window.Razorpay(options);
      razorpayInstance.open();

    } catch (error) {
      console.error("Error in handleAddMoney:", error);
      toast.error("Failed to add money to wallet");
    }
  };

  const rates = getCommissionRates();
  const walletData = {
    commissionRate: rates.vendorRate,
    totalWalletBalance: totalWalletBalance || 0,
    driversCount: driverWallets?.length || 0,
    adminRate: rates.adminRate,
    driverRate: rates.driverRate
  };

  if (loading || loadingCommission) {
    return (
      <div className="bg-gradient-to-br from-purple-50 to-indigo-100 rounded-xl shadow-lg p-6 border border-purple-200 animate-pulse">
        <div className="h-6 w-40 bg-gray-200 rounded mb-4"></div>
        <div className="h-20 bg-gray-200 rounded mb-4"></div>
        <div className="h-16 bg-gray-200 rounded"></div>
      </div>
    );
  }

  return (
    <>
    <div className="bg-gradient-to-br from-purple-50 to-indigo-100 rounded-xl shadow-lg p-6 border border-purple-200">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <div className="p-3 rounded-full bg-purple-100 text-purple-600 mr-3">
            <FaWallet className="w-6 h-6" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-800">Vendor Wallet</h3>
            <p className="text-sm text-gray-600">
              Commission Rate: {walletData.commissionRate}% per ride
            </p>
          </div>
        </div>
        <button
          onClick={() => setShowBalances(!showBalances)}
          className="p-2 rounded-full hover:bg-purple-200 transition-colors"
          title={showBalances ? "Hide balances" : "Show balances"}
        >
          {showBalances ? (
            <FaEye className="w-4 h-4 text-purple-600" />
          ) : (
            <FaEyeSlash className="w-4 h-4 text-purple-600" />
          )}
        </button>
      </div>

      {/* Main Wallet Stats */}
      <div className="mb-6">
        {/* Total Wallet Balance - Shows Commission Earned Amount */}
        <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-600">Total Wallet Balance</span>
            <FaWallet className="w-4 h-4 text-blue-500" />
          </div>
          <div className="flex items-center">
            <span className="text-3xl font-bold text-blue-600">
              {showBalances ? formatCurrency(vendorWalletBalance || 0) : "₹****"}
            </span>
          </div>
          <p className="text-sm text-gray-500 mt-2">
            Across {walletData.driversCount} drivers
          </p>
        </div>
      </div>

      {/* Commission Rate Highlight */}
      <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-4 border border-green-200 mb-6">
        <div className="text-center">
          <div className="text-4xl font-bold text-green-900 mb-2">
            {walletData.commissionRate}%
          </div>
          <p className="text-lg text-green-700 font-medium">Per completed ride</p>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="mb-4">
        <button
          onClick={() => setShowAddMoneyModal(true)}
          className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 px-4 rounded-lg font-medium transition-colors flex items-center justify-center"
        >
          <FaPlus className="mr-2" />
          Add Money to Wallet
        </button>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <button
          onClick={() => onNavigateToWallet && onNavigateToWallet('wallet')}
          className="bg-purple-500 hover:bg-purple-600 text-white py-3 px-4 rounded-lg font-medium transition-colors flex items-center justify-center"
        >
          <FaWallet className="mr-2" />
          Wallet Details
        </button>

        <button
          onClick={() => onNavigateToWallet && onNavigateToWallet('commission')}
          className="bg-green-500 hover:bg-green-600 text-white py-3 px-4 rounded-lg font-medium transition-colors flex items-center justify-center"
        >
          <FaMoneyBillWave className="mr-2" />
          Commission
        </button>

        <button
          onClick={() => window.location.href = '/vendor/wallet/history'}
          className="bg-blue-500 hover:bg-blue-600 text-white py-3 px-4 rounded-lg font-medium transition-colors flex items-center justify-center"
        >
          <FaHistory className="mr-2" />
          History
        </button>
      </div>

      {/* Commission Structure Info - Using Real Data */}
      <div className="mt-4 p-3 bg-purple-50 rounded-lg border border-purple-200">
        <h4 className="text-sm font-medium text-purple-800 mb-2">Commission Structure</h4>
        <div className="text-xs text-purple-700 space-y-1">
          <div className="flex justify-between">
            <span>Admin Commission:</span>
            <span className="font-medium">{walletData.adminRate}%</span>
          </div>
          <div className="flex justify-between">
            <span>Vendor Commission (You):</span>
            <span className="font-medium text-purple-900">{walletData.commissionRate}%</span>
          </div>
          <div className="flex justify-between">
            <span>Driver Gets:</span>
            <span className="font-medium">{walletData.driverRate}%</span>
          </div>
        </div>
        {commissionData && (
          <div className="mt-2 pt-2 border-t border-purple-200">
            <div className="text-xs text-purple-600">
              ✅ Real Data Source: {commissionData.commissionSettings?.isUsingGlobal ? 'Admin Global Settings' : 'Vendor Specific Settings'}
            </div>
            <div className="text-xs text-purple-500 mt-1">
              Updated from live admin system (not hardcoded)
            </div>
          </div>
        )}
      </div>

      {/* Last Updated */}
      <p className="text-xs text-gray-500 mt-3 text-center">
        Last updated: {new Date().toLocaleTimeString()}
      </p>
    </div>

    {/* Add Money Modal */}
    {showAddMoneyModal && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full m-4">
          <div className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Add Money to Your Wallet
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Amount (₹)
                </label>
                <input
                  type="number"
                  value={addMoneyAmount}
                  onChange={(e) => setAddMoneyAmount(e.target.value)}
                  placeholder="Enter amount"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-sm text-gray-600">
                  Current Balance: <span className="font-medium">{formatCurrency(vendorWalletBalance || 0)}</span>
                </p>
                {addMoneyAmount && (
                  <p className="text-sm text-gray-600">
                    New Balance: <span className="font-medium text-green-600">
                      {formatCurrency((vendorWalletBalance || 0) + parseFloat(addMoneyAmount || 0))}
                    </span>
                  </p>
                )}
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowAddMoneyModal(false);
                  setAddMoneyAmount("");
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAddMoney}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center"
                disabled={!addMoneyAmount || parseFloat(addMoneyAmount) < 100}
              >
                Proceed to Pay
              </button>
            </div>
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default VendorWalletDisplay;
